type AutocompleteResult = {value: any, text: string};

let _instances = new WeakMap<Element, Autocomplete>();

type AutocompleteOptions = {
    "uri": string,
    "delay": number,
    "onSelect": (r: AutocompleteResult) => any,
    "strict": boolean, // If true: only result from autocomplete result is accepted, display "No result"
}

class Autocomplete {
    elContainer: HTMLElement;
    elSearch:    HTMLInputElement;
    elMenu:      HTMLElement;
    elValue:     HTMLInputElement;

    elNoResult:  HTMLElement;

    filled: boolean = false;
    cursor: number = null;
    results: Array<AutocompleteResult>;

    onSelect: (r: AutocompleteResult) => any;

    uri: string;
    delay: number;
    timer: number;

    strict: boolean;

    constructor(container: Element, opts: Partial<AutocompleteOptions> = {}) {
        this.elContainer  = container as HTMLInputElement;
        this.elSearch     = container.querySelector('input.autocomplete-input') as HTMLInputElement;
        this.elMenu       = container.querySelector('.autocomplete-menu') as HTMLInputElement;

        this.elNoResult   = container.querySelector('.autocomplete-menu-noresult') as HTMLElement;

        if (this.elContainer.hasAttribute('data-autocomplete-target')) {
            this.elValue = document.querySelector(this.elContainer.getAttribute('data-autocomplete-target'));
        }

        if (this.elValue && this.elValue.value) {
            this.filled = true;
        }

        this.uri          = opts.uri;
        this.delay        = opts.delay || 100;
        this.strict       = opts.strict || false;

        this.onSelect     = opts.onSelect || this.defaultOnSelect

        this.results      = [];

        this.init();

        _instances.set(container, this);
    }

    static instance(el: Element): Autocomplete|null {
        return _instances.get(el);
    }

    init() {
        this.elSearch.addEventListener('input', (e) => {
            const q = this.elSearch.value;

            this.search(q)
        })

        this.elSearch.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key == "Backspace") {
                if (this.filled) {
                    this.filled = false;
                    this.onSelect(null)
                }
            } else if (e.key == "ArrowUp") {
                if (this.results.length > 0) {
                    if (this.cursor !== null) {
                        this.cursor = this.cursor > 0 ? this.cursor - 1 : 0;
                    } else {
                        this.cursor = 0;
                    }

                    this.highlightIndex(this.cursor)
                } else {
                    this.cursor = null;
                }
            } else if (e.key == "ArrowDown") { // down
                if (this.cursor !== null) {
                    this.cursor = this.cursor + 1
                } else {
                    this.cursor = 0;
                }

                if (this.cursor > this.results.length - 1) {
                    this.cursor = null;
                }

                this.highlightIndex(this.cursor)

            } else if (e.key == "Enter") { // enter
                if (this.cursor !== null) {
                    e.preventDefault();

                    this.selectIndex(this.cursor);
                }
            }

        });

        this.elSearch.addEventListener('focus', (e) => {
            this.elContainer.classList.add("active");

            if (this.results.length) {
                this.openMenu();
            }
        });

        this.elMenu.addEventListener('click', (e) => {
            const el = (e.target as Element);

            let elSelected;

            if (el.matches(".autocomplete-menu-item")) {
                elSelected = el;
            } else if (el.matches(".autocomplete-menu-item *")) {
                elSelected = el.closest(".autocomplete-menu-item");
            }

            if (elSelected) {
                if (elSelected.hasAttribute('data-index')) {
                    let i = elSelected.getAttribute('data-index');

                    this.selectIndex(parseInt(i));
                } else {
                    this.filled = false;
                    this.onSelect(null)
                }
            }
        })
    }

    setUrlParams(base: string, params: Map<string, string>): string {
        let url = new URL(base, window.location.href);

        let urlParams = url.searchParams;

        params.forEach((v: string, k: string) => {
            urlParams.set(k, v);
        });

        return url.toString();
    }

    search(q: string) {

        this.filled = false;

        if (q.length) {
            this.startLoading();

            this.throttle(async () => {
                const uri = this.setUrlParams(this.uri, new Map([
                    ["q", q],
                    ["_ts", Date.now().toString()],
                ]))

                const res = await fetch(uri)

                if (res.ok) {
                    const body = await res.json();

                    this.results = body.list;

                    this.displayResults();
                }

                this.endLoading()

            }, this.delay)
        } else {
            this.clearMenu();
        }

    }

    highlightIndex(i: number) {
        const all = this.elMenu.querySelectorAll(".autocomplete-menu-item");

        all.forEach((e, i) => {
            e.classList.remove('hover');
        });


        const el = this.elMenu.querySelector("[data-index='"+i+"']");

        if (el) {
            el.classList.add('hover');
        }
    }

    selectIndex(i: number) {
        const r = this.results[i];

        this.filled = true;

        this.onSelect(r);

        this.cursor  = null;
        this.results = [];
        this.closeMenu();
    }

    defaultOnSelect(r: AutocompleteResult) {
        this.elSearch.value = r ? r.text : "";

        if (this.elValue) {
            const oldValue = this.elValue.value;
            const newValue = r ? r.value : "";

            if (oldValue != newValue) {
                this.elValue.value = newValue;
                this.elValue.dispatchEvent(new Event("change"))
            }
        }
    }

    openMenu() {
        this.elMenu.classList.add('open');
    }

    closeMenu() {
        this.elMenu.classList.remove('open');
    }

    clearMenu() {
        this.elMenu.classList.remove('open');
        this.elMenu.innerHTML = "";
    }

    displayResults() {

        this.clearMenu();

        this.results.forEach((e, i) => {
            const li   = document.createElement("li");
            li.classList.add('autocomplete-menu-item')
            li.setAttribute('data-index', i+"")

            const text = document.createTextNode(e.text);
            li.appendChild(text);

            this.elMenu.appendChild(li);
        });

        if (this.results.length == 0 && this.elNoResult) {

            const li   = document.createElement("li");
            li.classList.add('autocomplete-menu-item');
            li.classList.add('autocomplete-menu-item-noresult');

            li.innerHTML = this.elNoResult.innerHTML;


            this.elMenu.appendChild(li);
        }

        this.openMenu();

    }

    afterSearch() {
        // Called when search is over
        this.elContainer.classList.remove("active");
        this.closeMenu();

        if (this.strict && !this.filled) {
            this.elSearch.value = "";
        }
    }

    startLoading() {

    }

    endLoading() {

    }

    throttle(f: () => void, delay: number) {
        clearTimeout(this.timer);

        this.timer = window.setTimeout(f, delay);
    }
}

// Close dropdowns
window.addEventListener('click', (e) => {
  const el = (e.target as Element);

  let thisAutocomplete: Autocomplete|null = null;

  if (el.matches(".autocomplete *")) {
    thisAutocomplete = Autocomplete.instance(el.closest(".autocomplete"));
  }

  // Close all other autocompletes
  const acs = document.querySelectorAll(".autocomplete.active");

  for (let i = 0; i < acs.length; i++) {
    const ac = Autocomplete.instance(acs[i]);

    if (ac !== thisAutocomplete) {
      ac.afterSearch();
    }
  }
})

export { Autocomplete, AutocompleteResult };